import type { IAPIAdapter } from 'plugins/tag/plugin_data_service/ts/Application/adapters/abstract/IAPIAdapter';
import { AbstractDataServiceFeature } from 'plugins/tag/plugin_data_service/ts/Application/abstract/AbstractDataServiceFeature';
import { formatEpisodesRemaining } from 'plugins/tag/plugin_data_service/ts/Application/features/UEC/formatEpisodesRemaining';
import { IDataServiceFeatureContext } from 'plugins/tag/plugin_data_service/ts/Application/interfaces/features/IDataServiceFeatureContext';
import { IUECFetchParams } from 'plugins/tag/plugin_data_service/ts/Application/interfaces/IUECFetchParams';
import { IUpNextEndpoint } from 'plugins/tag/plugin_data_service/ts/Application/interfaces/IUpNextEndpoint';
import { IParamsVO } from 'tag/ts/tag/interfaces/params/IParamsVO';
import { CONVEYOR_KEY_API_VIDEO_LOAD_PARAMS_VO } from 'tag/ts/tag/model/enums/ConveyorKeys';
import type { IUECPayload } from 'plugins/tag/plugin_data_service/ts/Application/interfaces/UEC/IUECPayload';
import type { IUECUpNextFetchResult } from 'plugins/tag/plugin_data_service/ts/Application/interfaces/UEC/IUECUpNextFetchResult';

/** Parsed JSON from the up-next adapter or site envelope (optional fields). */
type IUECRawUpNextBody = {
	apiResponse?: unknown;
};

/**
 * UEC content type values used by the up-next endpoint.
 */
const UEC_CONTENT_TYPES = {
	SHOW: 'show',
	MOVIE: 'movie',
	LIVE: 'live'
} as const;

/** Active adapter implementing {@link IUpNextEndpoint}, or null when missing or not capable. */
function upNextAdapter( adapter: IAPIAdapter | null ): IUpNextEndpoint | null {
	if ( adapter == null ) {
		return null;
	}

	if ( typeof ( adapter as unknown as IUpNextEndpoint ).fetchUpNextRecommendations !== 'function' ) {
		return null;
	}
	return adapter as unknown as IUpNextEndpoint;
}

/**
 * @copyright © Paramount Streaming
 * @description Data Service feature that exposes the UEC (Universal End Cards) up-next
 * recommendation endpoint via tag.API.DATA.UEC. The feature delegates to the active adapter's
 * IUpNextEndpoint implementation, mapping the Skin-layer payload to IUECFetchParams.
 */
export class UECFeature extends AbstractDataServiceFeature {
	#upNextAdapter: IUpNextEndpoint | null = null;

	get name (): string {
		return 'UECFeature';
	}

	get featureKey (): string {
		return 'UEC';
	}

	getPublicAPI (): Record<string, unknown> {
		return {
			fetchUpNextRecommendations: this.#fetchUpNextRecommendations
		};
	}

	protected doInstall( context: IDataServiceFeatureContext ): void {
		context.utils.Conveyor( CONVEYOR_KEY_API_VIDEO_LOAD_PARAMS_VO )
			.transformer( this.#resolveUpNextAdapter, { priority: 50 } );
	}

	#resolveUpNextAdapter = ( paramsVO: IParamsVO ): IParamsVO => {
		const activeAdapter: IAPIAdapter | null = this.getApiService().getActiveAdapter();
		this.#upNextAdapter = upNextAdapter( activeAdapter );

		if ( !this.#upNextAdapter ) {
			this.logger?.warn(
				'[UECFeature] UEC unavailable: no active adapter or it does not implement fetchUpNextRecommendations'
			);
		}
		return paramsVO;
	};

	/**
	 * @description Public-facing method exposed on tag.API.DATA.UEC.fetchUpNextRecommendations.
	 * Accepts the Skin-layer payload shape and maps it to IUECFetchParams before delegating
	 * to the adapter's IUpNextEndpoint implementation.
	 * Returns a response shaped for Skin consumption: { apiResponse, episodesRemaining? } (see #postProcess).
	 */
	#fetchUpNextRecommendations = async ( requestPayload: IUECPayload ): Promise<unknown> => {
		if ( !this.#upNextAdapter ) {
			throw new Error( '[UECFeature] up-next endpoint not available on the active adapter' );
		}

		const params = this.#mapPayloadToParams( requestPayload );
		this.logger?.log( '[UECFeature]', 'fetchUpNextRecommendations params:', params );

		const rawResponse = await this.#upNextAdapter.fetchUpNextRecommendations( params, this.context?.tag?.params );
		this.logger?.log( '[UECFeature]', 'fetchUpNextRecommendations response:', rawResponse as never );

		return this.#postProcess( rawResponse, requestPayload );
	};

	/**
	 * @description Normalizes adapter JSON into the skin/page shape. Site API may return
	 * `result: { apiResponse }` or only the inner body; upstream `apiEndpoint` labels are stripped.
	 */
	#postProcess = ( rawResponse: unknown, requestPayload: IUECPayload ): IUECUpNextFetchResult => {
		let apiResponse: unknown = rawResponse;

		if ( rawResponse && typeof rawResponse === 'object' ) {
			const body = rawResponse as IUECRawUpNextBody;
			if ( body.apiResponse !== undefined ) {
				apiResponse = body.apiResponse;
			}
		}

		// Malformed UEC: some feeds return `apiResponse: []` instead of `{ recommendations: [] }`.
		// Normalize only an empty array so CP-style non-empty `apiResponse` arrays are unchanged.
		if ( Array.isArray( apiResponse ) && apiResponse.length === 0 ) {
			this.logger?.warn( '[UECFeature] Normalizing empty apiResponse [] → { recommendations: [] }' );
			apiResponse = { recommendations: [] };
		}

		const result: IUECUpNextFetchResult = { apiResponse };
		const contentType = requestPayload?.contentType?.toLowerCase();

		if ( contentType && contentType !== UEC_CONTENT_TYPES.LIVE ) {
			const raw = apiResponse && typeof apiResponse === 'object' ? ( apiResponse as Record<string, unknown> ) : undefined;
			const nested = raw?.apiResponse && typeof raw.apiResponse === 'object'
				? ( raw.apiResponse as Record<string, unknown> )
				: undefined;
			const recommendationsForEpisodesRemaining = raw?.recommendations ?? nested?.recommendations;
			result.episodesRemaining = formatEpisodesRemaining(
				!!requestPayload?.cpEpisodesRemainingEnabled,
				recommendationsForEpisodesRemaining
			);
		}

		return result;
	};

	/**
	 * @description Maps the Skin-layer payload (from CardUtilities.getEndCardAPIPayload)
	 * to IUECFetchParams for the up-next endpoint.
	 *
	 * Mapping:
	 *   show  -> endCardId = seriesId,  contentType = 'show'
	 *   movie -> endCardId = contentId, contentType = 'movie'
	 *   live  -> endCardId = listingId, contentType = 'live', channelSlug required
	 */
	#mapPayloadToParams = ( payload: IUECPayload ): IUECFetchParams => {
		const normalizedType = payload?.contentType?.toLowerCase();

		let endCardId: string;
		let contentType: IUECFetchParams[ 'contentType' ];

		switch ( normalizedType ) {
			case UEC_CONTENT_TYPES.SHOW:
				endCardId = payload.seriesId ?? payload.contentId ?? '';
				contentType = UEC_CONTENT_TYPES.SHOW;
				break;
			case UEC_CONTENT_TYPES.MOVIE:
				endCardId = payload.contentId ?? '';
				contentType = UEC_CONTENT_TYPES.MOVIE;
				break;
			case UEC_CONTENT_TYPES.LIVE:
				endCardId = String( payload.listingId ?? '' );
				contentType = UEC_CONTENT_TYPES.LIVE;
				break;
			default:
				throw new Error( `[UECFeature] unsupported contentType: "${ payload?.contentType }"` );
		}

		if ( !endCardId ) {
			throw new Error( `[UECFeature] endCardId could not be resolved for contentType: "${ contentType }"` );
		}

		const params: IUECFetchParams = {
			endCardId,
			contentType
		};

		if ( payload._clientRegion ) {
			params._clientRegion = payload._clientRegion;
		}
		if ( payload.channelSlug ) {
			params.channelSlug = payload.channelSlug;
		}
		if ( payload.device ) {
			params.device = payload.device;
		}
		if ( payload.start !== undefined ) {
			params.start = payload.start;
		}
		if ( payload.rows !== undefined ) {
			params.rows = payload.rows;
		}
		if ( payload.nextItem === true ) {
			params.nextItem = true;
		}

		return params;
	};
}